import { useThemeStore } from "@stores/theme.store";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkPage } from "@utils/router";
import { PopupConfirm, PopupCustom } from "@components/general/popup";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { SchoolService } from "@services/school.service";

import { SemiCircle } from "@components/background/semi_circle";
import { ProfileAvatarHeader } from "@components/general/profile_avatar_header";
// import { androidSetupBiometric } from "@utils/android"; // biometric belum terpakai
// import { useBiometricStore } from "@stores/biometric.store"; // biometric belum terpakai
import {
  LuChevronRight,
  // LuFingerprint, // biometric belum terpakai
  LuKeyRound,
  LuLogOut,
  LuScanLine,
  LuUserCog,
} from "react-icons/lu";
import { Button } from "antd";
import JsBarcode from "jsbarcode";

const Section = ({ title }: { title: string }) => (
  <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2">
    <div className="tw-w-1 tw-h-4 tw-rounded-full tw-bg-primary tw-shrink-0" />
    <div className="tw-text-xs tw-font-bold tw-text-text-secondary tw-uppercase tw-tracking-widest">
      {title}
    </div>
  </div>
);

type MenuRowProps = {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  sub: string;
  onClick: () => void;
  danger?: boolean;
};

const MenuRow = ({ icon, iconBg, label, sub, onClick, danger }: MenuRowProps) => (
  <div
    className="tw-flex tw-items-center tw-gap-3 tw-py-3 tw-cursor-pointer"
    onClick={onClick}
  >
    <div
      className="tw-flex tw-items-center tw-justify-center tw-rounded-full tw-w-10 tw-h-10 tw-shrink-0 tw-border-2 tw-border-background-100 tw-shadow-sm"
      style={{ background: iconBg }}
    >
      {icon}
    </div>
    <div className="tw-flex-1 tw-min-w-0">
      <div
        className={`tw-text-sm tw-font-semibold tw-truncate ${
          danger ? "tw-text-red-500" : "tw-text-text-primary"
        }`}
      >
        {label}
      </div>
      <div className="tw-text-[10px] tw-text-text-secondary tw-truncate">{sub}</div>
    </div>
    <LuChevronRight
      className={`tw-shrink-0 tw-text-base ${
        danger ? "tw-text-red-400" : "tw-text-text-secondary"
      }`}
    />
  </div>
);

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { setHeader, setAppBar, popupStatus, setPopupStatus } = useThemeStore(
    (state) => state
  );
  // const [isBiometric, setIsBiometric] = useState<boolean>(false); // biometric belum terpakai
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  // const { isActiveBiometric } = useBiometricStore((state) => state); // biometric belum terpakai

  const { data: dataNis, isSuccess: isSuccessNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "---";
  const gender = dataNis?.data?.gender ?? "";

  const { data, isPending } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIOGRAPHY, nis],
    queryFn: () => SchoolService.studentBiography(nis),
  });

  useEffect(() => {
    setHeader({ buttonBack: true, filter: false, title: "Profil", image: false });
    setAppBar({ title: "Profil", show: true, leading: "back" });
  }, [setHeader, setAppBar]);

  // Biometric setup — belum terpakai, dinonaktifkan sementara
  // useEffect(() => {
  //   const skip = localStorage.getItem("is-biometric");
  //   setIsBiometric(skip === "true");
  // }, []);
  //
  // const onSetupBiometric = () => {
  //   const status = localStorage.getItem("status-biometric");
  //   if (status === "register") {
  //     androidSetupBiometric("remove");
  //   } else {
  //     androidSetupBiometric("register");
  //   }
  // };

  const onLogout = () => {
    Cookies.remove("token");
    Cookies.remove("redirect");
    navigate("/auth/login");
    setPopupStatus(false);
    setAppBar({ show: false });
  };

  const barcodeRef = useCallback(
    (node: SVGSVGElement) => {
      if (node && isSuccessNis && nis && nis !== "---") {
        node.innerHTML = "";
        try {
          JsBarcode(node as unknown as HTMLElement, nis, {
            format: "CODE128",
            width: 2.5,
            height: 100,
            displayValue: true,
          });
        } catch (error) {
          console.error("Barcode generation failed:", error);
        }
      }
    },
    [isSuccessNis, nis]
  );

  return (
    <>
      <SemiCircle />
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        <div className="tw-mb-4 tw-mt-14 tw-pt-12 tw-relative tw-text-center card-body">
          <ProfileAvatarHeader
            foto={data?.data?.foto}
            namasiswa={data?.data?.namasiswa}
            kelas={data?.data?.kelas}
            marhalah={data?.data?.marhalah}
            gender={gender}
            isPending={isPending}
          />

          <div className="tw-my-4 divider" />

          <div className="tw-text-left">
            <Section title="Umum" />

            <MenuRow
              icon={<LuUserCog className="tw-text-lg tw-text-[#6366f1]" />}
              iconBg="#e4e8ff"
              label="Edit Profil"
              sub="Ubah data informasi siswa"
              onClick={() => navigate(linkPage.SETTING_UPDATE_ACCOUNT)}
            />

            <div className="tw-h-px tw-mx-0" style={{ background: "#f1f1f1" }} />

            <MenuRow
              icon={<LuKeyRound className="tw-text-lg tw-text-[#3b82f6]" />}
              iconBg="#ecf4ff"
              label="Ubah Kata Sandi"
              sub="Perbarui kata sandi akun"
              onClick={() =>
                navigate(linkPage.SETTING_CHANGE_PASSWORD, { state: dataNis?.data })
              }
            />

            <div className="tw-h-px tw-mx-0" style={{ background: "#f1f1f1" }} />

            <MenuRow
              icon={<LuScanLine className="tw-text-lg tw-text-[#22c55e]" />}
              iconBg="#f0fdf4"
              label="Barcode"
              sub="Menampilkan barcode NIS"
              onClick={() => setModalOpen(true)}
            />
          </div>
        </div>

        {/* Preferences card */}
        <div className="card-body">
          <Section title="Preferensi" />

          <MenuRow
            icon={<LuLogOut className="tw-text-lg tw-text-[#ef4444]" />}
            iconBg="#ffecec"
            label="Keluar"
            sub="Keluar dari akun dengan aman"
            onClick={() => setPopupStatus(true)}
            danger
          />

          {/* Menu biometric — belum terpakai, dinonaktifkan sementara
          {isBiometric && (
            <>
              <div className="tw-h-px" style={{ background: "#f1f1f1" }} />
              <MenuRow
                icon={
                  <LuFingerprint
                    className={`tw-text-lg ${
                      isActiveBiometric ? "tw-text-[#0d9488]" : "tw-text-[#64748b]"
                    }`}
                  />
                }
                iconBg={isActiveBiometric ? "#e0f9f2" : "#f1f5f9"}
                label={isActiveBiometric ? "Login Biometrik Aktif" : "Aktifkan Login Biometrik"}
                sub={
                  isActiveBiometric
                    ? "Login cepat biometrik sudah aktif"
                    : "Fitur login cepat dengan biometrik"
                }
                onClick={onSetupBiometric}
              />
            </>
          )}
          */}
        </div>
      </div>

      <PopupConfirm
        open={popupStatus}
        title={dataNis?.data?.nama ?? "-"}
        message="Yakin ingin keluar dari Sipond?"
        textConfirm="Keluar"
        textCancel="Batalkan"
        onConfirm={onLogout}
        onCancel={() => setPopupStatus(false)}
      />

      <PopupCustom
        slideUpModal={false}
        open={modalOpen}
        title="Barcode NIS"
        onClose={() => setModalOpen(false)}
      >
        <div className="tw-flex tw-flex-col tw-items-center">
          <svg key={nis} ref={barcodeRef} />
          <Button
            onClick={() => setModalOpen(false)}
            className="tw-mt-6 tw-h-10 tw-w-full tw-text-sm tw-rounded-xl tw-bg-red-500 tw-text-white focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
          >
            Tutup
          </Button>
        </div>
      </PopupCustom>
    </>
  );
};
